// src/app/components/onboarding/stage2-record-views.tsx
/**
 * The record behind a Stage 2 step, readable: the prompt as its sections
 * (instructions, lists, the JSON shape asked for), the reply as a JSON tree,
 * the sources as cards with their text, and the system prompt as the brand's
 * facts, the rules and the sources. Every view has a "Raw" switch that shows
 * the text exactly as it was sent, which is what the record is.
 */
import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, FileText, Globe } from "lucide-react";

const muted: React.CSSProperties = { color: "var(--muted-foreground)", fontSize: "var(--text-xs)" };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11 };
const box: React.CSSProperties = {
  border: "1px solid var(--border)", borderRadius: 8, padding: 12, backgroundColor: "var(--background)",
};
const codeBox: React.CSSProperties = {
  ...mono, whiteSpace: "pre-wrap", margin: 0, padding: 10, borderRadius: 6, maxHeight: 360, overflow: "auto",
  backgroundColor: "var(--muted)", border: "1px solid var(--border)",
};
const smallChip: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 4, padding: "0 7px", borderRadius: 999, fontSize: 10,
  fontWeight: 600, border: "1px solid var(--border)", color: "var(--muted-foreground)", whiteSpace: "nowrap",
};

/** A small Formatted / Raw switch above a view. */
function WithRaw({ raw, children }: { raw: string; children: React.ReactNode }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div>
      <div className="flex justify-end gap-1 mb-1">
        {(["Formatted", "Raw"] as const).map((label) => {
          const on = (label === "Raw") === showRaw;
          return (
            <button key={label} onClick={() => setShowRaw(label === "Raw")}
              style={{ ...smallChip, cursor: "pointer", backgroundColor: on ? "var(--muted)" : "transparent",
                color: on ? "var(--foreground)" : "var(--muted-foreground)" }}>
              {label}
            </button>
          );
        })}
      </div>
      {showRaw ? <pre style={codeBox}>{raw}</pre> : children}
    </div>
  );
}

// ── The prompt ──────────────────────────────────────────────────────────────

type Block =
  | { kind: "title"; text: string }
  | { kind: "para"; text: string }
  | { kind: "list"; items: string[] }
  | { kind: "label"; text: string }
  | { kind: "code"; text: string };

const looksLikeJson = (line: string) => /^\s*[[{]/.test(line);

/** Prompt text into blocks: the "Step: x." title, paragraphs, "- " lists,
 *  a line ending in ":" as a label, and JSON as code. */
function blocksOf(text: string): Block[] {
  const blocks: Block[] = [];
  const paragraphs = text.split(/\n{2,}/);
  for (const raw of paragraphs) {
    const lines = raw.split("\n").filter((l) => l.trim() !== "");
    if (!lines.length) continue;
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (/^Step: .+\.$/.test(line.trim())) {
        blocks.push({ kind: "title", text: line.trim().replace(/^Step: /, "").replace(/\.$/, "") });
        i += 1;
      } else if (looksLikeJson(line)) {
        blocks.push({ kind: "code", text: lines.slice(i).join("\n") });
        i = lines.length;
      } else if (/^\s*- /.test(line)) {
        const items: string[] = [];
        while (i < lines.length && (/^\s*- /.test(lines[i]) || /^\s{2,}\S/.test(lines[i]))) {
          if (/^\s*- /.test(lines[i])) items.push(lines[i].replace(/^\s*- /, ""));
          else items[items.length - 1] += ` ${lines[i].trim()}`;
          i += 1;
        }
        blocks.push({ kind: "list", items });
      } else if (/:$/.test(line.trim()) && line.trim().length < 90 && i + 1 < lines.length) {
        blocks.push({ kind: "label", text: line.trim().replace(/:$/, "") });
        i += 1;
      } else {
        const para: string[] = [];
        while (i < lines.length && !/^\s*- /.test(lines[i]) && !looksLikeJson(lines[i])
          && !(/:$/.test(lines[i].trim()) && lines[i].trim().length < 90 && i + 1 < lines.length)) {
          para.push(lines[i]);
          i += 1;
        }
        blocks.push({ kind: "para", text: para.join(" ") });
      }
    }
  }
  return blocks;
}

/** "key: rest" list items get the key in bold. */
function ListItem({ text }: { text: string }) {
  const m = /^([a-z_][a-z0-9_ ]{0,30}):\s(.*)$/i.exec(text);
  return (
    <li style={{ fontSize: "var(--text-xs)", color: "var(--foreground)", marginBottom: 3 }}>
      {m ? <><code style={{ ...mono, fontWeight: 700 }}>{m[1]}</code>: {m[2]}</> : text}
    </li>
  );
}

function CodeBlock({ text }: { text: string }) {
  const pretty = useMemo(() => {
    try { return JSON.stringify(JSON.parse(text), null, 2); } catch { return text; }
  }, [text]);
  return <pre style={codeBox}>{pretty}</pre>;
}

export function PromptView({ text }: { text: string }) {
  const blocks = useMemo(() => blocksOf(text || ""), [text]);
  return (
    <WithRaw raw={text}>
      <div style={box} className="space-y-2">
        {blocks.map((b, i) => {
          switch (b.kind) {
            case "title":
              return <div key={i} style={{ fontSize: "var(--text-sm)", fontWeight: 700 }}>Step · {b.text}</div>;
            case "label":
              return <div key={i} style={{ ...muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 6 }}>{b.text}</div>;
            case "list":
              return <ul key={i} style={{ margin: "0 0 0 18px", listStyle: "disc" }}>{b.items.map((t, j) => <ListItem key={j} text={t} />)}</ul>;
            case "code":
              return <CodeBlock key={i} text={b.text} />;
            default:
              return <p key={i} style={{ fontSize: "var(--text-xs)", lineHeight: 1.6, margin: 0, color: "var(--foreground)" }}>{b.text}</p>;
          }
        })}
      </div>
    </WithRaw>
  );
}

// ── The reply ───────────────────────────────────────────────────────────────

function parseReply(text?: string | null): any | undefined {
  if (!text) return undefined;
  const start = text.indexOf("{");
  try { return JSON.parse(start >= 0 ? text.slice(start, text.lastIndexOf("}") + 1) : text); } catch { return undefined; }
}

function Leaf({ value }: { value: any }) {
  if (value === null) return <span style={{ ...mono, color: "var(--muted-foreground)" }}>null</span>;
  if (typeof value === "boolean") return <span style={{ ...mono, color: value ? "var(--alpha-teal)" : "var(--infrared-coral)", fontWeight: 700 }}>{String(value)}</span>;
  if (typeof value === "number") return <span style={{ ...mono, color: "var(--signal-blue)" }}>{value}</span>;
  return <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)" }}>{String(value)}</span>;
}

/** A row's title in a list of objects: its id and label, label, name or text. */
function summaryOf(value: any): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  const name = value.label || value.name || value.text || value.claim || value.quote || "";
  return [value.id, typeof name === "string" ? (name.length > 80 ? `${name.slice(0, 79)}…` : name) : ""]
    .filter(Boolean).join(" · ");
}

function Node({ name, value, depth }: { name?: string; value: any; depth: number }) {
  const isObject = value && typeof value === "object";
  const [open, setOpen] = useState(depth < 2);
  const keyLabel = name !== undefined && (
    <span style={{ ...mono, fontWeight: 700, color: "var(--foreground)", marginRight: 6 }}>{name}</span>
  );
  if (!isObject) {
    return <div className="flex items-start" style={{ paddingLeft: depth ? 14 : 0, marginBottom: 2 }}>{keyLabel}<Leaf value={value} /></div>;
  }
  const entries: [string, any][] = Array.isArray(value)
    ? value.map((v, i) => [String(i + 1), v])
    : Object.entries(value);
  const count = Array.isArray(value) ? `${value.length} item${value.length === 1 ? "" : "s"}` : `${entries.length} field${entries.length === 1 ? "" : "s"}`;
  const summary = summaryOf(value);
  return (
    <div style={{ paddingLeft: depth ? 14 : 0, marginBottom: 2 }}>
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-left"
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {keyLabel}
        {summary && <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)" }}>{summary}</span>}
        <span style={{ ...muted, marginLeft: 4 }}>{count}</span>
      </button>
      {open && (
        <div style={{ borderLeft: "1px solid var(--border)", marginLeft: 5, paddingTop: 2 }}>
          {entries.map(([k, v]) => <Node key={k} name={k} value={v} depth={depth + 1} />)}
        </div>
      )}
    </div>
  );
}

export function ReplyView({ text, error }: { text?: string | null; error?: string | null }) {
  if (error) {
    return <div style={{ ...box, color: "var(--infrared-coral)", fontSize: "var(--text-xs)" }}>The call failed: {error}</div>;
  }
  const parsed = parseReply(text);
  return (
    <WithRaw raw={text || ""}>
      {parsed === undefined ? (
        <div style={box}>
          <div style={{ ...muted, color: "var(--infrared-coral)", marginBottom: 6 }}>Not valid JSON; shown as sent.</div>
          <pre style={codeBox}>{text}</pre>
        </div>
      ) : (
        <div style={{ ...box, maxHeight: 480, overflow: "auto" }}><Node value={parsed} depth={0} /></div>
      )}
    </WithRaw>
  );
}

// ── Sources and the system prompt ───────────────────────────────────────────

export interface SourceRow {
  ref: string | null; url: string; label: string; chars: number; included: boolean; truncated?: boolean;
}

/** {ref -> text} from the <source id="S1" ...> blocks of the system prompt. */
function sourceTexts(system: string): Record<string, string> {
  const out: Record<string, string> = {};
  const re = /<source id="([^"]+)"[^>]*>\n?([\s\S]*?)\n?<\/source>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(system || ""))) out[m[1]] = m[2];
  return out;
}

/** Source text with "# " headings drawn as headings. */
function SourceText({ text }: { text: string }) {
  return (
    <div style={{ maxHeight: 360, overflow: "auto" }} className="space-y-1">
      {text.split("\n").filter((l) => l.trim()).map((line, i) => {
        const h = /^(#{1,4})\s+(.*)$/.exec(line);
        if (h) {
          return <div key={i} style={{ fontSize: h[1].length === 1 ? "var(--text-sm)" : "var(--text-xs)", fontWeight: 700, marginTop: 6 }}>{h[2]}</div>;
        }
        return <p key={i} style={{ fontSize: "var(--text-xs)", lineHeight: 1.6, margin: 0, color: "var(--foreground)" }}>{line}</p>;
      })}
    </div>
  );
}

function SourceCard({ source, text }: { source: SourceRow; text?: string }) {
  const [open, setOpen] = useState(false);
  const isPage = /^https?:\/\//.test(source.url || "");
  return (
    <div style={{ ...box, padding: 10 }}>
      <div className="flex items-center gap-2 flex-wrap">
        <span style={{ ...smallChip, color: "var(--alpha-teal)", borderColor: "var(--alpha-teal)" }}>{source.ref ?? "—"}</span>
        {isPage ? <Globe className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />}
        {isPage ? (
          <a href={source.url} target="_blank" rel="noreferrer"
            style={{ fontSize: "var(--text-xs)", fontWeight: 600, color: "var(--signal-blue)" }}>
            {source.url} <ExternalLink className="w-3 h-3 inline" />
          </a>
        ) : (
          <span style={{ fontSize: "var(--text-xs)", fontWeight: 600 }}>{source.label || source.url}</span>
        )}
        <span style={muted}>{source.chars.toLocaleString()} characters</span>
        {source.truncated && <span style={smallChip}>cut to fit</span>}
        {!source.included && <span style={{ ...smallChip, color: "var(--infrared-coral)", borderColor: "var(--infrared-coral)" }}>left out: over budget</span>}
        {text !== undefined && (
          <button onClick={() => setOpen((o) => !o)}
            style={{ ...smallChip, cursor: "pointer", marginLeft: "auto", background: "none" }}>
            {open ? "Hide text" : "Show text"}
          </button>
        )}
      </div>
      {open && text !== undefined && <div className="mt-2"><SourceText text={text} /></div>}
    </div>
  );
}

export function SourcesView({ sources, systemPrompt }: { sources: SourceRow[]; systemPrompt?: string | null }) {
  const texts = useMemo(() => sourceTexts(systemPrompt || ""), [systemPrompt]);
  if (!sources.length) return <p style={muted}>No sources were recorded for this generation.</p>;
  return (
    <div className="space-y-2">
      {sources.slice(0, 50).map((s, i) => <SourceCard key={i} source={s} text={s.ref ? texts[s.ref] : undefined} />)}
      {sources.length > 50 && <p style={muted}>and {sources.length - 50} more</p>}
    </div>
  );
}

/** The system prompt as the brand's facts ("The brand: x" lines), the
 *  instructions, and the sources. */
export function SystemPromptView({ text }: { text?: string | null }) {
  const system = text || "";
  const parsed = useMemo(() => {
    const before = system.split("<sources>")[0];
    const facts: [string, string][] = [];
    const rules: string[] = [];
    for (const para of before.split(/\n{2,}/)) {
      const lines = para.split("\n").filter((l) => l.trim());
      const factLines = lines.filter((l) => /^[A-Z][A-Za-z ,']{1,60}: /.test(l));
      if (lines.length > 1 && factLines.length === lines.length) {
        for (const l of lines) {
          const at = l.indexOf(": ");
          facts.push([l.slice(0, at), l.slice(at + 2)]);
        }
      } else if (lines.length) {
        rules.push(lines.join(" "));
      }
    }
    const sources: { id: string; from: string; text: string }[] = [];
    const re = /<source id="([^"]+)" from="([^"]*)">\n?([\s\S]*?)\n?<\/source>/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(system))) sources.push({ id: m[1], from: m[2], text: m[3] });
    return { facts, rules, sources };
  }, [system]);
  if (!system) return <p style={muted}>Not recorded.</p>;
  return (
    <WithRaw raw={system}>
      <div className="space-y-3">
        {parsed.rules[0] && <p style={{ fontSize: "var(--text-xs)", lineHeight: 1.6, margin: 0 }}>{parsed.rules[0]}</p>}
        {parsed.facts.length > 0 && (
          <div style={box}>
            <div style={{ ...muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>The brand, as the model was told</div>
            <div className="grid gap-x-4 gap-y-1" style={{ gridTemplateColumns: "max-content 1fr" }}>
              {parsed.facts.map(([k, v]) => (
                <React.Fragment key={k}>
                  <span style={{ ...muted, fontWeight: 600 }}>{k}</span>
                  <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)" }}>{v}</span>
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
        {parsed.rules.length > 1 && (
          <div style={box}>
            <div style={{ ...muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>Rules</div>
            <ul style={{ margin: "0 0 0 18px", listStyle: "disc" }}>
              {parsed.rules.slice(1).map((r, i) => <li key={i} style={{ fontSize: "var(--text-xs)", lineHeight: 1.6, marginBottom: 4 }}>{r}</li>)}
            </ul>
          </div>
        )}
        {parsed.sources.length > 0 && (
          <div style={box}>
            <div style={{ ...muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
              Sources in the prompt ({parsed.sources.length})
            </div>
            <div className="space-y-2">
              {parsed.sources.map((s) => (
                <SourceCard key={s.id} source={{ ref: s.id, url: s.from, label: s.from, chars: s.text.length, included: true }} text={s.text} />
              ))}
            </div>
          </div>
        )}
      </div>
    </WithRaw>
  );
}
